import { useMemo } from 'react'
import { parseAsString, useQueryState } from 'nuqs'

export function toDateOnly(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export function addDays(date: Date, n: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + n)
  return d
}

export function toIsoDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function addDaysSkipSunday(date: Date, n: number): Date {
  if (n === 0) return date
  const step = n > 0 ? 1 : -1
  let remaining = Math.abs(n)
  let cur = date
  while (remaining > 0) {
    cur = addDays(cur, step)
    if (cur.getDay() !== 0) remaining -= 1 // 0 = Sunday
  }
  return cur
}

function snapOffSunday(date: Date): Date {
  return date.getDay() === 0 ? addDays(date, 1) : date
}

/**
 * Returns today as the anchor. If today is Sunday, snaps to the next Monday.
 */
export function getInitialAnchor(today: Date): Date {
  return snapOffSunday(toDateOnly(today))
}

export function useWorkerCalendarDays() {
  const today = toDateOnly(new Date())

  const [anchorIso, setAnchorIso] = useQueryState(
    'calAnchor',
    parseAsString.withDefault(toIsoDate(getInitialAnchor(today))),
  )

  const anchor = useMemo(() => {
    const [y, m, d] = anchorIso.split('-').map(Number)
    const parsed = new Date(y, m - 1, d)
    const valid = Number.isNaN(parsed.getTime()) ? getInitialAnchor(today) : parsed
    return snapOffSunday(valid)
  }, [anchorIso, today])

  const days = useMemo(() => [
    anchor,
    addDaysSkipSunday(anchor, 1),
    addDaysSkipSunday(anchor, 2),
  ], [anchor])

  const from = useMemo(
    () => new Date(days[0].getFullYear(), days[0].getMonth(), days[0].getDate(), 0, 0, 0, 0).toISOString(),
    [days],
  )
  const to = useMemo(
    () => new Date(days[2].getFullYear(), days[2].getMonth(), days[2].getDate(), 23, 59, 59, 999).toISOString(),
    [days],
  )

  const todayIso = toIsoDate(today)
  const dayIsos = days.map(toIsoDate)

  function next() {
    setAnchorIso(toIsoDate(addDaysSkipSunday(anchor, 1)))
  }

  function prev() {
    setAnchorIso(toIsoDate(addDaysSkipSunday(anchor, -1)))
  }

  function goToToday() {
    setAnchorIso(toIsoDate(getInitialAnchor(today)))
  }

  return { days, dayIsos, from, to, todayIso, prev, next, goToToday }
}
